import { addBreadcrumb } from '@sentry/react';
import type {
	JSX,
	ReactNode,
	MouseEvent,
	MouseEventHandler,
	ReactElement,
} from 'react';
import { Children, Fragment } from 'react';
import styled from 'styled-components';

import * as styles from 'pkg/config/styles';

import { cssClasses } from 'pkg/css/utils';
import { deprecationWarning } from 'pkg/debug/deprecation';
import useTooltip from 'pkg/hooks/useTooltip';
import Link from 'pkg/router/Link';

import MaterialSymbol, { IconSizeMap } from 'components/material-symbols';

import { EllipsisIcon } from 'components/loaders/spinner';
import type { MaterialSymbolVariant } from 'components/material-symbols/symbols';

import * as css from './styles.css';

export type ButtonVariant =
	| 'tertiary'
	| 'transparent'
	| 'primary'
	| 'secondary'
	| 'caution'
	| 'text'
	| 'confirm';

export type ButtonTypes = 'button' | 'submit' | 'reset';

export type ButtonSize = 'default' | 'small' | 'large';

interface RouterLinkParams {
	[key: string]: string | boolean | number;
}

export interface ButtonComponentProps {
	children?: ReactNode | ReactNode[];
	className?: string;
	onClick?: (event?: MouseEvent | TouchEvent | PointerEvent) => void;
	/** Shows a loading spinner and disables the button. */
	busy?: boolean;
	disabled?: boolean;
	href?: string;
	active?: boolean;
	pressed?: boolean;
	type?: ButtonTypes;
	variant?: ButtonVariant;
	size?: ButtonSize;
	noPadding?: boolean;
	round?: boolean;
	tooltip?: string;
	testid?: string;

	onMouseEnter?: (e: MouseEvent) => void;

	block?: boolean;
	icon?: MaterialSymbolVariant;
	hasIcon?: boolean;
	iconSize?: number;
	iconPosition?: 'left' | 'right' | 'top';
	iconRotate?: string;
	'data-testid'?: string;
	spaceBetween?: boolean;
	fullWidth?: boolean;

	/** @deprecated Pass the label as children instead. */
	label?: string;
	/** @deprecated Use `busy` instead. */
	isLoading?: boolean;
	/** @deprecated Use `size="small"` instead. */
	small?: boolean;
	/** @deprecated Use `size="large"` instead. */
	large?: boolean;
	/** @deprecated Use `variant="transparent"` instead. */
	transparent?: boolean;
	/** @deprecated Use `variant="primary"` instead. */
	primary?: boolean;
	/** @deprecated Use `variant="secondary"` instead. */
	secondary?: boolean;
	/** @deprecated Use `variant="caution"` instead. */
	caution?: boolean;
	/** @deprecated Use `variant="confirm"` instead. */
	confirm?: boolean;
	/** @deprecated Use `variant="text"` instead. */
	text?: boolean;
	/** @deprecated Has no effect; remove. */
	brand?: boolean;
	/** @deprecated Use `type="submit"` instead. */
	submit?: boolean;
}

type ButtonVariantProps = Pick<
	ButtonComponentProps,
	'variant' | 'confirm' | 'caution' | 'secondary' | 'primary' | 'transparent'
>;

type ButtonSizeProps = Pick<ButtonComponentProps, 'size' | 'small' | 'large'>;

export function resolveButtonSize(props: ButtonSizeProps): ButtonSize {
	if (props.size) {
		return props.size;
	}

	if (props.small) {
		return 'small';
	}

	if (props.large) {
		return 'large';
	}

	return 'default';
}

export function resolveButtonVariant(
	props: ButtonVariantProps,
	fallback: ButtonVariant = 'tertiary'
): ButtonVariant {
	if (props.variant) {
		return props.variant;
	}

	if (props.confirm) {
		return 'confirm';
	}

	if (props.caution) {
		return 'caution';
	}

	if (props.secondary) {
		return 'secondary';
	}

	if (props.primary) {
		return 'primary';
	}

	if (props.transparent) {
		return 'transparent';
	}

	return fallback;
}

function warnDeprecatedProps(props: ButtonComponentProps): void {
	if (props.label !== undefined) {
		deprecationWarning('Button', 'label', 'pass the label as children instead');
	}

	if (props.isLoading !== undefined) {
		deprecationWarning('Button', 'isLoading', 'use busy instead');
	}

	if (props.small !== undefined || props.large !== undefined) {
		deprecationWarning(
			'Button',
			'small/large',
			'use size="small" or size="large" instead'
		);
	}

	if (
		props.transparent ||
		props.primary ||
		props.secondary ||
		props.caution ||
		props.confirm
	) {
		deprecationWarning(
			'Button',
			'variant booleans',
			'use variant="..." instead'
		);
	}

	if (props.text) {
		deprecationWarning('Button', 'text', 'use variant="text" instead');
	}

	if (props.brand) {
		deprecationWarning('Button', 'brand', 'has no effect; remove');
	}

	if (props.submit !== undefined) {
		deprecationWarning('Button', 'submit', 'use type="submit" instead');
	}
}

function Button(props: ButtonComponentProps): JSX.Element {
	warnDeprecatedProps(props);

	let icon = null;
	let iconPosition = 'left';

	const size = resolveButtonSize(props);
	const busy = props.busy || props.isLoading;

	let defaultIconSize = IconSizeMap.default;
	if (size === 'small') {
		defaultIconSize = IconSizeMap.small;
	} else if (size === 'large') {
		defaultIconSize = IconSizeMap.large;
	}

	if (props.icon?.length > 1) {
		// A caller-provided iconSize is a scale multiplier (legacy); without
		// one the icon gets a fixed pixel size matched to the button size.
		icon = (
			<MaterialSymbol
				className={css.materialSymbol}
				variant={props.icon}
				scale={props.iconSize}
				size={props.iconSize ? undefined : defaultIconSize}
				rotate={props.iconRotate ? +props.iconRotate : undefined}
			/>
		);

		if (props.iconPosition) {
			iconPosition = props.iconPosition;
		}
	}

	let children: ReactNode[] = [];

	if (props.children) {
		children = Children.toArray(props.children);
	} else if (props.label) {
		children = [props.label];
	}

	const variant = resolveButtonVariant(props);

	const isIconOnly = !children.length && props.icon;

	const btnCssClasses = cssClasses(
		css.button,
		css[variant],
		props.className,
		isIconOnly ? css.iconOnly : '',
		busy ? css.loading : '',
		props.block ? css.block : '',
		size === 'small' ? css.small : '',
		size === 'large' ? css.large : '',
		props.text ? css.text : '',
		!!props.icon || props.hasIcon ? css['has-icon'] : '',
		iconPosition === 'right' ? css['icon-position-right'] : '',
		iconPosition === 'left' ? css['icon-position-left'] : '',
		iconPosition === 'top' ? css['icon-position-top'] : '',
		busy ? css.busy : '',
		props.active ? css.active : '',
		props.noPadding ? css.noPadding : '',
		children.length > 0 ? css['has-content'] : '',
		props.spaceBetween ? css.spaceBetween : '',
		props.round ? css.round : '',
		props.fullWidth === false ? css.compact : ''
	);
	const linkProps: {
		href?: string;
		params?: RouterLinkParams | Record<string, unknown>;
	} = {};

	if (props.href) {
		linkProps.href = props.href;
	}

	const { onMouseEnter, tooltip } = useTooltip(props.tooltip);

	const hasLinkProps = Object.keys(linkProps).length > 0;

	const content = (
		<Fragment>
			{busy && <EllipsisIcon className={css.ellipsis} />}
			{iconPosition !== 'right' ? icon : null}
			{children.length > 0 ? (
				<span className={css.label}>
					<div className={css.innerLabel}>{children}</div>
				</span>
			) : null}
			{iconPosition === 'right' ? icon : null}
			{tooltip}
		</Fragment>
	);

	const onClick: MouseEventHandler<HTMLElement> = (e) => {
		const target = e.target as HTMLElement;
		let elementText = target.tagName;

		if (props.label) {
			elementText = props.label;
		} else if (children.length > 0) {
			const filteredChildren = Children.toArray(children).filter(
				(c: ReactElement) => c.type !== MaterialSymbol
			);

			if (filteredChildren.length === 1) {
				// This is for the cases when we got <Button>{t('Translation')}</Button> which is quite often
				elementText = filteredChildren[0] as string;
			}
		}

		const breadcrumbData: { [key: string]: string } = {
			tagName: target.tagName,
			page: window.location.pathname,
		};

		if (Object.keys(target.dataset).length > 0) {
			breadcrumbData.dataset = JSON.stringify(target.dataset);
		}

		addBreadcrumb({
			category: 'ui.click',
			message: `Clicked on Button - ${elementText}`,
			level: 'info',
			data: {
				tagName: target.tagName,
				page: window.location.pathname,
			},
		});

		if (props.onClick) {
			props.onClick(e);
		}
	};

	const sharedButtonProps = {
		onClick,
		className: btnCssClasses,
		disabled: busy || props.disabled,
		'aria-pressed': props.pressed,
		onMouseEnter: onMouseEnter,
		'data-testid': props.testid || props['data-testid'],
	};

	return hasLinkProps ? (
		<Link {...linkProps} {...sharedButtonProps}>
			{content}
		</Link>
	) : (
		<button
			{...sharedButtonProps}
			type={props.type ?? (props.submit ? 'submit' : 'button')}>
			{content}
		</button>
	);
}

interface ButtonGroupProps {
	spacing?: styles.spacing;
	align?: 'start' | 'center' | 'end';
	justifyContent?: 'start' | 'center' | 'end' | 'stretch';
}

// Grid layout with spacing between each button, e.g. `[button] [button] [button]`
const ButtonGroup = styled.div<ButtonGroupProps>`
	display: grid;
	grid-auto-flow: column;
	grid-auto-columns: auto;
	justify-items: ${({ justifyContent }) =>
		justifyContent ? justifyContent : 'start'};
	justify-content: ${({ justifyContent }) =>
		justifyContent ? justifyContent : 'start'};
	grid-gap: ${({ spacing }) => (spacing ? spacing : styles.spacing._3)};
	align-items: ${({ align }) => (align ? align : 'start')};
`;

interface FilterButtonProps {
	active?: boolean;
	testid?: string;
	onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

const FilterButton: React.FC<FilterButtonProps> = ({
	active,
	onClick,
	testid,
}) => {
	return (
		<Button
			active={active}
			onClick={onClick}
			testid={testid}
			icon="filter_list"
			iconSize={1.8}
			className={css.filterButton}
		/>
	);
};

export { ButtonGroup, FilterButton };

export default Button;
